/**
 * Round-trip latency calibration. Mandatory, and it runs before any scoring.
 *
 * Without it every onset is wrong by the output buffer + speaker + air + mic +
 * input buffer delay — typically 20-60ms wired, which is half the width of the
 * scoring curve. Scoring uncalibrated would mean measuring the sound card.
 *
 * Method: emit a short click, record through the mic, cross-correlate the
 * recording against the click envelope. The lag at peak correlation IS the
 * round-trip latency. Repeat and take the median, because one click can be
 * swallowed by a cough or a buffer glitch.
 */

import type { CalibrationResult, ScoringConfig } from "@catchup/shared";

export interface CalibrationProgress {
  click: number;
  total: number;
  measured?: number;
  confidence?: number;
}

/** A click with a sharp attack — needs to be broadband to correlate well. */
function makeClick(ctx: AudioContext, durationSec = 0.006): AudioBuffer {
  const n = Math.floor(ctx.sampleRate * durationSec);
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < n; i++) {
    // Exponentially decaying noise burst: broadband so it survives the
    // speaker/mic path, short so the correlation peak is unambiguous.
    const env = Math.exp(-i / (n * 0.25));
    data[i] = (Math.random() * 2 - 1) * env;
  }
  return buf;
}

/**
 * Normalised cross-correlation of the recording against the click's energy
 * envelope. Correlating envelopes rather than raw waveforms makes this robust
 * to the phase inversion and comb filtering a real speaker/mic path adds.
 */
function crossCorrelate(
  recorded: Float32Array,
  click: Float32Array,
  maxLagSamples: number,
): { lag: number; confidence: number } {
  const env = (x: Float32Array, win: number): Float32Array => {
    const out = new Float32Array(x.length);
    let acc = 0;
    for (let i = 0; i < x.length; i++) {
      acc += x[i]! * x[i]!;
      if (i >= win) acc -= x[i - win]! * x[i - win]!;
      out[i] = Math.sqrt(acc / Math.min(i + 1, win));
    }
    return out;
  };

  const win = Math.max(8, Math.floor(click.length / 2));
  const recEnv = env(recorded, win);
  const clkEnv = env(click, win);

  // Remove the DC/noise floor so a constantly noisy room doesn't correlate
  // with everything equally well.
  const demean = (x: Float32Array) => {
    let m = 0;
    for (const v of x) m += v;
    m /= x.length || 1;
    const out = new Float32Array(x.length);
    for (let i = 0; i < x.length; i++) out[i] = x[i]! - m;
    return out;
  };

  const r = demean(recEnv);
  const c = demean(clkEnv);

  let cNorm = 0;
  for (const v of c) cNorm += v * v;
  cNorm = Math.sqrt(cNorm);
  if (cNorm === 0) return { lag: 0, confidence: 0 };

  let bestLag = 0;
  let bestScore = -Infinity;
  const limit = Math.min(maxLagSamples, r.length - c.length);

  for (let lag = 0; lag < limit; lag++) {
    let dot = 0;
    let rNorm = 0;
    for (let i = 0; i < c.length; i++) {
      const rv = r[lag + i]!;
      dot += rv * c[i]!;
      rNorm += rv * rv;
    }
    rNorm = Math.sqrt(rNorm);
    if (rNorm === 0) continue;
    const score = dot / (rNorm * cNorm);
    if (score > bestScore) {
      bestScore = score;
      bestLag = lag;
    }
  }

  return { lag: bestLag, confidence: Math.max(0, bestScore) };
}

export interface CalibrationDeps {
  ctx: AudioContext;
  /** Worklet node running capture-processor.js, already wired to the mic. */
  capture: AudioWorkletNode;
  config: ScoringConfig;
  outputLabel: string;
  suspectedWireless: boolean;
  onProgress?: (p: CalibrationProgress) => void;
}

export async function calibrate(deps: CalibrationDeps): Promise<CalibrationResult> {
  const { ctx, capture, config, outputLabel, suspectedWireless } = deps;
  const clicks = config.capture.calibrationClicks;
  const measurements: Array<{ latency: number; confidence: number }> = [];

  const clickBuf = makeClick(ctx);
  const clickData = clickBuf.getChannelData(0);
  const captureSeconds = 0.6;

  for (let i = 0; i < clicks; i++) {
    const result = await new Promise<{ latency: number; confidence: number } | null>(
      (resolve) => {
        const onMessage = (e: MessageEvent) => {
          const msg = e.data;
          if (msg.type === "started") {
            // Schedule the click a little after capture is confirmed running,
            // so the click can never land before the first recorded sample.
            const src = ctx.createBufferSource();
            src.buffer = clickBuf;
            src.connect(ctx.destination);
            const at = ctx.currentTime + 0.12;
            src.start(at);
            (capture as unknown as { __clickAt?: number }).__clickAt = at;
            (capture as unknown as { __captureStart?: number }).__captureStart =
              msg.startTime;
            return;
          }
          if (msg.type !== "capture") return;
          capture.port.removeEventListener("message", onMessage);

          const samples: Float32Array = msg.samples;
          const sr: number = msg.sampleRate;
          const captureStart: number =
            (capture as unknown as { __captureStart?: number }).__captureStart ??
            msg.startTime;
          const clickAt =
            (capture as unknown as { __clickAt?: number }).__clickAt ?? 0;

          // Only search forward from the click, and only as far as a sane
          // latency could be. Searching the whole buffer invites a spurious
          // peak on a random room noise transient.
          const clickOffsetSamples = Math.max(
            0,
            Math.round((clickAt - captureStart) * sr),
          );
          const searchFrom = clickOffsetSamples;
          const searchLen = Math.min(
            samples.length - searchFrom,
            Math.round(0.4 * sr),
          );
          if (searchLen <= clickData.length) {
            resolve(null);
            return;
          }

          const window = samples.subarray(searchFrom, searchFrom + searchLen);
          const { lag, confidence } = crossCorrelate(
            window,
            clickData,
            searchLen - clickData.length,
          );
          resolve({ latency: lag / sr, confidence });
        };

        capture.port.addEventListener("message", onMessage);
        capture.port.start?.();
        capture.port.postMessage({ type: "start", seconds: captureSeconds });

        setTimeout(() => {
          capture.port.removeEventListener("message", onMessage);
          resolve(null);
        }, 4000);
      },
    );

    if (result) {
      measurements.push(result);
      deps.onProgress?.({
        click: i + 1,
        total: clicks,
        measured: result.latency,
        confidence: result.confidence,
      });
    } else {
      deps.onProgress?.({ click: i + 1, total: clicks });
    }

    await new Promise((r) => setTimeout(r, 180));
  }

  const usable = measurements.filter(
    (m) => m.confidence >= config.capture.minCalibrationConfidence,
  );
  const pool = usable.length >= 2 ? usable : measurements;

  if (pool.length === 0) {
    return {
      latency: 0,
      confidence: 0,
      outputLabel,
      suspectedWireless,
      sampleRate: ctx.sampleRate,
      measuredAt: new Date().toISOString(),
    };
  }

  // Median, not mean: one click swallowed by a buffer glitch produces a wild
  // outlier, and with five samples a mean would carry it straight through.
  const sorted = [...pool].sort((a, b) => a.latency - b.latency);
  const mid = Math.floor(sorted.length / 2);
  const latency =
    sorted.length % 2
      ? sorted[mid]!.latency
      : (sorted[mid - 1]!.latency + sorted[mid]!.latency) / 2;
  const confidence =
    pool.reduce((s, m) => s + m.confidence, 0) / pool.length;

  return {
    latency,
    confidence,
    outputLabel,
    suspectedWireless,
    sampleRate: ctx.sampleRate,
    measuredAt: new Date().toISOString(),
  };
}

/** Why a calibration result is or isn't good enough to score on. */
export function calibrationVerdict(
  cal: CalibrationResult,
  config: ScoringConfig,
): { ok: boolean; reason?: string } {
  if (cal.suspectedWireless) {
    return {
      ok: false,
      reason:
        `"${cal.outputLabel}" looks like a Bluetooth output. A2DP adds 150-300ms ` +
        "and the delay drifts during playback, so a single calibration constant " +
        "cannot correct it. Switch to wired headphones to score.",
    };
  }
  if (cal.confidence < config.capture.minCalibrationConfidence) {
    return {
      ok: false,
      reason:
        `Couldn't hear the calibration click clearly (confidence ${cal.confidence.toFixed(2)}). ` +
        "Turn the volume up, keep the room quiet, and make sure the mic isn't muted.",
    };
  }
  if (cal.latency > config.capture.maxCalibratedLatency) {
    return {
      ok: false,
      reason:
        `Measured ${(cal.latency * 1000).toFixed(0)}ms round-trip latency, above the ` +
        `${(config.capture.maxCalibratedLatency * 1000).toFixed(0)}ms limit. That's ` +
        "usually a wireless output or a very large audio buffer.",
    };
  }
  return { ok: true };
}

/**
 * Heuristic wireless detection from the device label. It's a heuristic because
 * the Web Audio API exposes no transport information at all — there is no
 * reliable way to ask "is this Bluetooth". Calibration catches what this
 * misses, via the latency ceiling.
 */
export function looksWireless(label: string): boolean {
  return /bluetooth|airpod|wireless|\bbt\b|headset|buds|beats|galaxy buds|wf-|wh-/i.test(
    label,
  );
}
